import React, { useState, useEffect } from 'react';
import { MaskedInput, createDefaultMaskGenerator } from 'react-hook-mask';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { doGET } from '../../../../util/HttpUtil';
import "./styles.css"
import { INPUT_AUTOFILL_PROPS } from '../../../../components/InputField/autofillProps';
import { useTranslation } from '../../../../store/context/TranslationContext';
import PreviousValue from '../../../../components/InputField/PreviousValue';
import { renderMaskedString, maskSSN } from '../../../../components/InputField/helper';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../../components/InputField/testId';

function generateRandomString(length) {
    const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        result += characters.charAt(randomIndex);
    }
    return result;
}

export default function SSNFieldMasked({
    id,
    className,
    label = "Social Security Number",
    value: initialValue,
    onChange,
    isDisabled,
    notifyParent,
    validateSSNFromCyclos = true,
    showErr,
    required,
    group = "Individuals_Lite",
    err,
    applicationId,
    prevValue,
    masking = true  // New prop with default value true
}) {
    const [value, setValue] = useState('');
    const [error, setError] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isMasked, setIsMasked] = useState(masking); // State to track masking
    const [isFocused, setIsFocused] = useState(false); // Track focus state
    const { t } = useTranslation()
    const fieldTestId = resolveAutomationTestId(id, label, "ssn");

    useEffect(() => {
        setError(err);
    }, [err]);

    useEffect(() => {
        setHasError(showErr);
    }, [showErr]);

    useEffect(() => {
        if (initialValue && value !== initialValue) {
            setValue(initialValue);
            validateSSNField(initialValue);
        }
    }, [initialValue]);

    const validateSSN = (ssn) => {
        const re = /^(?!000|666)[0-8][0-9]{2}(?!00)[0-9]{2}(?!0000)[0-9]{4}$/;
        return re.test(ssn.replace(/-/g, ''));
    };

    const handleChange = (newValue) => {
        if (initialValue != newValue) {
            setValue(newValue);
            onChange(newValue);
            setError(null);
            setHasError(false);
        }
    };

    const validateSSNField = async (ssnParam) => {
        if (!ssnParam) return;

        if (!validateSSN(ssnParam)) {
            setError("Please enter a valid SSN");
            setHasError(true);
            notifyParent(false);
            return;
        }

        if (validateSSNFromCyclos) {
            setLoading(true);
            try {
                const formattedSSN = ssnParam.replace(/-/g, '');
                const response = await doGET(`/api/cyclos/verify/field?group=${group}&value=${formattedSSN}&field=Individual_SSN&applicationId=${applicationId}`);

                if (response.status === 204) {
                    notifyParent(true);
                    setError(null);
                    setHasError(false);
                } else if (response.status === 200 && response.data) {
                    setError(response.data);
                    setHasError(true);
                    notifyParent(false);
                }
            } catch (e) {
                // Transient backend/Cyclos failure (network, 5xx). Don't leave the field stuck on
                // "Validating..." or let the rejection escape; the backend re-verifies on submit.
                // eslint-disable-next-line no-console
                console.error('SSN verification request failed', e);
            } finally {
                setLoading(false);
            }
        }
    };

    const formatSSNForTyping = (value, maskValue) => {
        if (!value || typeof value !== 'string') return "";

        const digitsOnly = value.replace(/\D/g, '');
        const limitedDigits = digitsOnly.slice(0, 9);

        let result = "";
        let digitIndex = 0;

        for (let i = 0; i < 11 && digitIndex < limitedDigits.length; i++) {
            if (i === 3 || i === 6) {
                result += "-";
            } else {
                const digit = limitedDigits[digitIndex];
                result += digitIndex < 5 ? (maskValue ? 'X' : digit) : digit;
                digitIndex++;
            }
        }

        return result;
    };

    const toggleMasking = () => {
        setIsMasked(!isMasked);
    };

    const maskGenerator = createDefaultMaskGenerator('999-99-9999');
    const resolvedId = id || generateRandomString(36);


    return (
        <div className={`${className} ${(error & hasError) ? 'error' : ''} `} data-testid={fieldTestId}>
            <div className='inputfield__container'>
                <div className='inputfield__innercontainer_outline'>
                    <Label id={resolvedId} label={t(label)} required={required} />
                    <div className={`inputfield px-0 fv_ssn_field_wrapper`}
                        style={{
                            margin: "0px 0px 5px 0px",
                            height: "50px",
                            position: "relative"
                        }}>
                        {masking && !isFocused ? (
                            // Masked display when not focused
                            <div
                                className={`fv_ssn_masked_display ${isDisabled ? "cursor-disabled" : ""}`}
                                onClick={() => !isDisabled && setIsFocused(true)}
                                style={{
                                    height: "50px",
                                    borderRadius: "15px",
                                    paddingLeft: "15px",
                                    paddingRight: "45px",
                                    border: "none",
                                    fontSize: "14px",
                                    backgroundColor: prevValue?.has ? "#FFF9D6" : isDisabled ? "#F6F6F6" : "#F1FAFC",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: isDisabled ? "not-allowed" : "text"
                                }}
                            >
                                {isMasked ? maskSSN(value, true) : formatSSNForTyping(value, false)}
                            </div>
                        ) : (
                            // Input container with overlay for masked typing
                            <div style={{ position: "relative" }}>
                                <MaskedInput
                                    id={resolvedId}
                                    data-testid={buildAutomationChildTestId(fieldTestId, "input")}
                                    {...INPUT_AUTOFILL_PROPS}
                                    className={`inputfield__input fv_ssn_input ${isDisabled ? "cursor-disabled" : ""}`}
                                    maskGenerator={maskGenerator}
                                    value={value}
                                    onChange={handleChange}
                                    onBlur={() => {
                                        validateSSNField(value);
                                        setIsFocused(false);
                                    }}
                                    onFocus={() => setIsFocused(true)}
                                    placeholder="XXX-XX-XXXX"
                                    disabled={isDisabled}
                                    type="text"
                                    style={{
                                        height: "50px",
                                        borderRadius: "15px",
                                        paddingLeft: "15px",
                                        paddingRight: masking ? "30px" : "15px",
                                        border: "none",
                                        fontSize: "14px",
                                        outline: "none",
                                        backgroundColor: prevValue?.has ? "#FFF9D6" : isDisabled ? "#F6F6F6" : "#F1FAFC",
                                        width: "100%",
                                        color: isMasked ? "transparent" : "inherit", // Hide actual text when masked
                                        caretColor: isMasked ? "#000" : "inherit", // Keep cursor visible
                                    }}
                                />

                                {isMasked && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "15px",
                                            right: "45px",
                                            letterSpacing: "1px",
                                            height: "50px",
                                            display: "flex",
                                            alignItems: "center",
                                            pointerEvents: "none", 
                                        }}
                                    >
                                        {formatSSNForTyping(value, true)}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Eye icon for toggling masking */}
                        {masking && (
                            <button
                                type="button"
                                className="fv_ssn_toggle_btn"
                                data-testid={buildAutomationChildTestId(fieldTestId, "toggle")}
                                onClick={toggleMasking}
                                // disabled={isDisabled}
                                style={{
                                    position: "absolute",
                                    right: "15px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    cursor: isDisabled ? "not-allowed" : "pointer",
                                    padding: "5px",
                                    opacity: isDisabled ? 0.5 : 1,
                                    color: "#666"
                                }}
                            >
                                {isMasked ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                            </button>
                        )}
                    </div>
                </div>
                {/* Error marker inside inputfield__container so the V2 :has() rule can turn the
                    outline box red, consistent with every other field. */}
                {(error?.length && hasError) ? <em className="selectfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>{t(error)}</em> : null}
            </div>
            <PreviousValue
                value={masking && isMasked ? maskSSN(prevValue?.value, true) : renderMaskedString(prevValue?.value, "XXX-XX-XXXX")}
                show={prevValue?.has}
                testId={buildAutomationChildTestId(fieldTestId, "previous")}
            />
            {loading && <div className="loading mt-2 ms-2 font-size-12" data-testid={buildAutomationChildTestId(fieldTestId, "loading")}>{t("Validating...")}</div>}
        </div>
    );
}

const Label = ({ label, required, id }) => {
    return (
        <label htmlFor={id}
            style={{
                minWidth: "120px",
                fontSize: 16,
                margin: "0px 0px 5px 5px",
                fontWeight: "500",
                textAlign: "left",
                display: 'block',
                marginBottom: '5px'
            }}>
            {label ?? "Username"}
            {required && <font color="red">*</font>}
        </label>
    );
};
